export type AuthMetadataRole = string | null | undefined;
export type HomeRoute = "/admin" | "/coach/dashboard-v2" | "/client";

type ResolveHomeRouteInput = {
  role: AuthMetadataRole;
  hasAdmin: boolean;
  hasCoach: boolean;
  hasClient: boolean;
};

function normalizeRole(role: AuthMetadataRole): string | null {
  if (typeof role !== "string") return null;
  const normalized = role.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}

export function isClientMetadataRole(role: AuthMetadataRole): boolean {
  const normalized = normalizeRole(role);
  return normalized === "regular" || normalized === "client" || normalized === "solo";
}

export function isCoachMetadataRole(role: AuthMetadataRole): boolean {
  return normalizeRole(role) === "coach";
}

export function isAdminMetadataRole(role: AuthMetadataRole): boolean {
  return normalizeRole(role) === "admin";
}

export function getMetadataPreferredHomeRoute(role: AuthMetadataRole): HomeRoute | null {
  if (isAdminMetadataRole(role)) return "/admin";
  if (isClientMetadataRole(role)) return "/client";
  if (isCoachMetadataRole(role)) return "/coach/dashboard-v2";
  return null;
}

export function resolveHomeRoute(input: ResolveHomeRouteInput): HomeRoute {
  const { role, hasAdmin, hasCoach, hasClient } = input;
  const normalizedRole = normalizeRole(role);

  if (isAdminMetadataRole(normalizedRole) && hasAdmin) return "/admin";
  if (isClientMetadataRole(normalizedRole) && hasClient) return "/client";
  if (isCoachMetadataRole(normalizedRole) && hasCoach) return "/coach/dashboard-v2";

  if (hasAdmin && !hasCoach && !hasClient) return "/admin";
  if (hasClient && !hasCoach) return "/client";
  if (hasCoach && !hasClient) return "/coach/dashboard-v2";

  // Ambiguous account (has both or has neither with stale profile state):
  // default to client surface to avoid accidental coach escalation.
  return "/client";
}
